import React from 'react'
import ReactDOM from 'react-dom/client'

// iOS PWA: cuando un chunk falla por deploy nuevo, forzar recarga
window.addEventListener('vite:preloadError', () => { window.location.reload() })
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import toast from 'react-hot-toast'
import { registerSW } from 'virtual:pwa-register'
import * as Sentry from '@sentry/react'
import App from './App'
import './styles/globals.css'

// ── Sentry — monitoreo de errores en producción ──────────────────────────────
// Variable Vercel: VITE_SENTRY_DSN  (sentry.io → Project → Settings → DSN)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn:              import.meta.env.VITE_SENTRY_DSN,
    environment:      import.meta.env.MODE,
    release:          import.meta.env.VITE_APP_VERSION ?? '1.0.0',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText:   false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate:  0.2,   // 20% de navegaciones trackeadas
    replaysOnErrorSampleRate: 1.0,  // 100% de sesiones con error grabadas
    replaysSessionSampleRate: 0.05, // 5% de sesiones normales
    // No enviar errores en desarrollo
    beforeSend: (event) => {
      if (import.meta.env.DEV) return null
      return event
    },
  })
}

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Sentry.ErrorBoundary
          fallback={({ error, resetError }) => (
            <div className="min-h-screen flex items-center justify-center bg-aqua-50 p-6">
              <div className="bg-white rounded-2xl shadow-aqua-md p-8 max-w-md w-full text-center">
                <div className="text-5xl mb-4">🐠</div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Algo salió mal</h2>
                <p className="text-sm text-gray-500 mb-6">
                  Ocurrió un error inesperado. Ya fue reportado automáticamente.
                </p>
                <button
                  onClick={resetError}
                  className="bg-aqua-500 text-white px-6 py-2.5 rounded-xl font-bold text-sm hover:bg-aqua-600 transition-colors"
                >
                  Reintentar
                </button>
              </div>
            </div>
          )}
        >
          <App />
        </Sentry.ErrorBoundary>
        <Toaster
          position="bottom-right"
          toastOptions={{
            style: {
              background: '#0f2878',
              color: '#fff',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: 14,
              borderRadius: 12,
              padding: '12px 18px',
            },
            success: { iconTheme: { primary: '#22c97a', secondary: '#fff' } },
            error: { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
          }}
        />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
)

// PWA: al haber versión nueva, NO recargar sola — avisar y dejar que el usuario
// decida cuándo actualizar (así nunca interrumpe una venta en curso).
const updateSW = registerSW({
  onNeedRefresh() {
    toast(
      (t) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          Nueva versión disponible
          <button
            onClick={() => { toast.dismiss(t.id); updateSW(true) }}
            style={{ background: '#22c97a', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 700, cursor: 'pointer' }}
          >
            Actualizar
          </button>
        </span>
      ),
      { duration: Infinity, id: 'sw-update' },
    )
  },
})
